import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { MorningBriefCard } from "../components/MorningBriefCard";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Euro, TrendingUp, TrendingDown, CalendarDays, UtensilsCrossed, AlertTriangle, Clock, Sparkles, ShieldAlert } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";

const fmt = (n) => `€ ${(n || 0).toFixed(2)}`;

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="p-5 rounded-xl border border-border/70 hover:-translate-y-0.5 transition-transform" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-wide uppercase text-muted-foreground">{label}</p>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="font-dash text-2xl font-semibold mt-3">{value}</p>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [trend, setTrend] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [insp, setInsp] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [expiry, setExpiry] = useState(null);
  const [reco, setReco] = useState(null);
  const canAccounting = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    api.get("/stats").then(({ data }) => setS(data)).catch(() => {});
    api.get("/haccp/expiry-dashboard").then(({ data }) => setExpiry(data)).catch(() => {});
    if (canAccounting) api.get("/stats/trend").then(({ data }) => setTrend(data)).catch(() => {});
    if (canAccounting) api.get("/stats/monthly-income?months=12").then(({ data }) => setMonthly(data)).catch(() => {});
    if (canAccounting) api.get("/analytics/recommendations").then(({ data }) => setReco(data)).catch(() => {});
    if (user?.role === "admin") api.get("/settings/inspection-token").then(({ data }) => setInsp(data)).catch(() => {});
  }, [canAccounting]);

  const refreshInsp = () => api.get("/settings/inspection-token").then(({ data }) => setInsp(data)).catch(() => {});
  const dismissAlert = async (id) => {
    await api.post(`/sales/stock-alerts/${id}/dismiss`).catch(() => {});
    setS((prev) => ({ ...prev, sales_alerts: prev.sales_alerts.filter((a) => a.id !== id) }));
  };

  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const { data } = await api.get("/ai/haccp-alerts");
      setAlerts(data);
    } catch {
      setAlerts({ alerts: [], summary: "Impossibile generare gli avvisi ora." });
    } finally {
      setAlertsLoading(false);
    }
  };
  useEffect(() => { loadAlerts(); }, []);

  if (!s) return <div className="text-muted-foreground">Caricamento…</div>;

  return (
    <div>
      <PageHeader title="Panoramica" subtitle="Situazione del locale in tempo reale" testid="dashboard-header" />

      {canAccounting && <MorningBriefCard />}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {canAccounting && <Stat icon={TrendingUp} label="Incassi oggi" value={fmt(s.income_today)} color="text-[hsl(var(--success))]" testid="stat-income-today" />}
        {canAccounting && <Stat icon={TrendingDown} label="Spese oggi" value={fmt(s.expense_today)} color="text-destructive" testid="stat-expense-today" />}
        {canAccounting && <Stat icon={Euro} label="Utile totale" value={fmt(s.profit_total)} color="text-primary" testid="stat-profit" />}
        <Stat icon={CalendarDays} label="Prenotazioni oggi" value={s.reservations_today} color="text-[hsl(var(--warning))]" testid="stat-reservations" />
      </div>

      {canAccounting && reco && reco.shown > 0 && (
        <Card className="p-6 rounded-xl mb-8" data-testid="reco-analytics-card">
          <div className="flex items-center gap-2 mb-4 text-primary">
            <Sparkles className="w-5 h-5" />
            <h3 className="font-dash font-semibold text-lg">Raccomandazioni AI</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div data-testid="reco-shown"><p className="text-2xl font-semibold font-dash">{reco.shown}</p><p className="text-xs text-muted-foreground">Suggerimenti mostrati</p></div>
            <div data-testid="reco-accepted"><p className="text-2xl font-semibold font-dash">{reco.accepted}</p><p className="text-xs text-muted-foreground">Accettati</p></div>
            <div data-testid="reco-conversion"><p className="text-2xl font-semibold font-dash">{reco.conversion_rate}%</p><p className="text-xs text-muted-foreground">Conversion rate</p></div>
            <div data-testid="reco-added-value"><p className="text-2xl font-semibold font-dash text-[hsl(var(--success))]">{fmt(reco.estimated_added_value)}</p><p className="text-xs text-muted-foreground">Valore aggiunto stimato</p></div>
            <div data-testid="reco-dismissed"><p className="text-2xl font-semibold font-dash">{reco.dismissed}</p><p className="text-xs text-muted-foreground">Ignorati / rifiutati</p></div>
            <div data-testid="reco-avg-increment"><p className="text-2xl font-semibold font-dash text-[hsl(var(--success))]">{fmt(reco.avg_increment)}</p><p className="text-xs text-muted-foreground">Incremento medio ordine</p></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border/60 p-3 flex items-center justify-around flex-wrap gap-y-2">
              <div className="text-center"><p className="text-xs text-muted-foreground">AOV con AI</p><p className="font-dash font-semibold text-lg">{fmt(reco.aov_with)}</p></div>
              <div className="w-px h-8 bg-border" />
              <div className="text-center"><p className="text-xs text-muted-foreground">AOV senza AI</p><p className="font-dash font-semibold text-lg">{fmt(reco.aov_without)}</p></div>
              <div className="w-px h-8 bg-border" />
              <div className="text-center"><p className="text-xs text-muted-foreground">Carrello prima</p><p className="font-dash font-semibold text-lg">{fmt(reco.avg_cart_before)}</p></div>
              <div className="w-px h-8 bg-border" />
              <div className="text-center"><p className="text-xs text-muted-foreground">Carrello dopo</p><p className="font-dash font-semibold text-lg">{fmt(reco.avg_cart_after)}</p></div>
            </div>
            <div className="rounded-lg border border-border/60 p-3">
              <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">Prodotti aggiunti più spesso</p>
              {reco.top_products?.length ? (
                <ul className="space-y-1">
                  {reco.top_products.slice(0, 5).map((t, i) => (
                    <li key={i} className="flex justify-between text-sm"><span>{t.name}</span><span className="text-muted-foreground">{t.count}×</span></li>
                  ))}
                </ul>
              ) : <p className="text-sm text-muted-foreground">Ancora nessun dato.</p>}
            </div>
          </div>
          <div className="mt-4 rounded-lg border border-border/60 p-3" data-testid="reco-pairings">
            <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">Top abbinamenti</p>
            {reco.pairings_insufficient ? (
              <p className="text-sm text-muted-foreground" data-testid="reco-pairings-insufficient">Dati insufficienti: servono almeno {reco.min_sample || 5} visualizzazioni per abbinamento.</p>
            ) : (
              <ul className="space-y-1">
                {(reco.top_pairings || []).map((p, i) => (
                  <li key={i} className="flex justify-between text-sm gap-3" data-testid={`reco-pairing-${i}`}>
                    <span className="truncate">{p.anchor} <span className="text-muted-foreground">→</span> {p.recommended}</span>
                    <span className="text-muted-foreground shrink-0">{p.conversion_rate}% · {p.accepted}/{p.shown}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {reco.ab_test_active && reco.ab_variants && (
            <div className="mt-4 rounded-lg border border-border/60 p-3" data-testid="reco-ab">
              <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">A/B test attivo</p>
              <div className="grid grid-cols-3 gap-2">
                {["control", "A", "B"].map((v) => (
                  <div key={v} className="text-center" data-testid={`reco-ab-${v}`}>
                    <p className="text-xs text-muted-foreground">{v}</p>
                    <p className="font-dash font-semibold">{reco.ab_variants[v].conversion_rate}%</p>
                    <p className="text-[11px] text-muted-foreground">{reco.ab_variants[v].accepted}/{reco.ab_variants[v].shown}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-xl" data-testid="low-stock-card">
          <div className="flex items-center gap-2 mb-4 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            <h3 className="font-dash font-semibold text-lg">Scorte in esaurimento</h3>
          </div>
          {s.sales_alerts?.length > 0 && (
            <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 p-3" data-testid="sales-stock-alerts">
              <p className="text-xs uppercase font-semibold text-destructive mb-2">Sotto soglia dopo lo scarico da vendite</p>
              <ul className="space-y-1.5">
                {s.sales_alerts.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-sm" data-testid={`sales-alert-${a.id}`}>
                    <span className="flex-1"><b>{a.name}</b> → {a.quantity} {a.unit} (min {a.min_quantity}) <span className="text-muted-foreground">· {a.product} · {a.date}</span></span>
                    <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => dismissAlert(a.id)} data-testid={`sales-alert-dismiss-${a.id}`}>OK</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.low_stock.length === 0 ? (
            <p className="text-sm text-muted-foreground">Tutte le scorte sono nella norma.</p>
          ) : (
            <ul className="space-y-2">
              {s.low_stock.map((i) => (
                <li key={i.id} className="flex justify-between text-sm border-b border-border/60 pb-2">
                  <span>{i.name}</span>
                  <span className="text-destructive font-medium">{i.quantity} {i.unit}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6 rounded-xl" data-testid="expiring-card">
          <div className="flex items-center gap-2 mb-4 text-[hsl(var(--warning))]">
            <Clock className="w-5 h-5" />
            <h3 className="font-dash font-semibold text-lg">Scadenze HACCP</h3>
            <Link to="/magazzino" className="ml-auto text-xs text-primary underline" data-testid="expiry-open-lots">Lotti</Link>
          </div>
          {expiry ? (
            <div className="grid grid-cols-4 gap-2 mb-4" data-testid="expiry-buckets">
              {[["expired", "Scaduti", "text-destructive"], ["today", "Oggi", "text-destructive"], ["d3", "Entro 3 gg", "text-[hsl(var(--warning))]"], ["d7", "Entro 7 gg", "text-foreground"]].map(([k, l, c]) => (
                <div key={k} className="rounded-lg bg-secondary/50 p-2 text-center" data-testid={`expiry-${k}`}><p className={`font-dash text-2xl font-semibold ${c}`}>{expiry.counts[k]}</p><p className="text-[10px] uppercase text-muted-foreground">{l}</p></div>
              ))}
            </div>
          ) : null}
          {expiry && [...expiry.expired, ...expiry.today, ...expiry.d3, ...expiry.d7].length > 0 && (
            <ul className="space-y-2 max-h-56 overflow-y-auto">
              {[...expiry.expired, ...expiry.today, ...expiry.d3, ...expiry.d7].slice(0, 12).map((l) => (
                <li key={l.id} className="flex justify-between gap-2 text-sm border-b border-border/60 pb-2" data-testid={`expiry-lot-${l.id}`}>
                  <span className="truncate">{l.product}<span className="text-xs text-muted-foreground"> · lotto {l.lotto || "—"}</span></span>
                  <span className={`whitespace-nowrap font-medium ${l.days_left < 0 ? "text-destructive" : l.days_left <= 3 ? "text-[hsl(var(--warning))]" : ""}`}>{l.days_left < 0 ? "SCADUTO" : l.days_left === 0 ? "oggi" : `${l.days_left} gg`} · {l.expiry}</span>
                </li>
              ))}
            </ul>
          )}
          {expiry && expiry.counts.expired + expiry.counts.today + expiry.counts.d3 + expiry.counts.d7 === 0 && <p className="text-sm text-muted-foreground">Nessun lotto in scadenza nei prossimi 7 giorni.</p>}
        </Card>
        <Card className="p-6 rounded-xl hidden" data-testid="expiring-card-old">
          <div className="flex items-center gap-2 mb-4 text-[hsl(var(--warning))]">
            <Clock className="w-5 h-5" />
            <h3 className="font-dash font-semibold text-lg">Scadenze imminenti (7 gg)</h3>
          </div>
          {s.expiring.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun prodotto in scadenza.</p>
          ) : (
            <ul className="space-y-2">
              {s.expiring.map((i) => (
                <li key={i.id} className="flex justify-between text-sm border-b border-border/60 pb-2">
                  <span>{i.name}</span>
                  <span className="text-[hsl(var(--warning))] font-medium">{i.expiry_date}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-6 p-6 rounded-xl" data-testid="ai-haccp-alerts-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-primary">
            <ShieldAlert className="w-5 h-5" />
            <h3 className="font-dash font-semibold text-lg">Avvisi HACCP (AI)</h3>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={loadAlerts} disabled={alertsLoading} data-testid="refresh-haccp-alerts-button">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> {alertsLoading ? "Analizzo…" : "Aggiorna"}
          </Button>
        </div>
        {alertsLoading && <p className="text-sm text-muted-foreground">L'AI sta analizzando scadenze, scorte e controlli…</p>}
        {!alertsLoading && alerts?.summary && <p className="text-sm text-[hsl(var(--success))]">{alerts.summary}</p>}
        {!alertsLoading && alerts?.alerts?.length > 0 && (
          <ul className="space-y-2">
            {alerts.alerts.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm bg-[hsl(var(--warning))]/10 rounded-lg p-3" data-testid={`haccp-alert-${i}`}>
                <AlertTriangle className="w-4 h-4 text-[hsl(var(--warning))] mt-0.5 shrink-0" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {user?.role === "admin" && insp && (
        <Card className="mt-6 p-6 rounded-xl border-primary/40" data-testid="inspection-access-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wide text-primary flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> Accesso controllo ispettivo</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">Comunica questa password all'ispettore per aprire il portale dal QR. <b>Cambia automaticamente dopo ogni accesso</b>.</p>
            </div>
            <div className="text-right">
              <p className="font-dash text-3xl font-semibold tracking-[0.3em]" data-testid="inspection-code">{insp.code}</p>
              <Button size="sm" variant="outline" className="rounded-full mt-2" onClick={refreshInsp} data-testid="refresh-inspection-code">Aggiorna</Button>
            </div>
          </div>
        </Card>
      )}

      {canAccounting && monthly.length > 0 && (
        <Card className="mt-6 p-6 rounded-xl" data-testid="monthly-income-chart-card">
          <h3 className="font-dash font-semibold text-lg mb-4">Incassi ufficiali vs preventivi (black) — 12 mesi</h3>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5C5C54" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#5C5C54" }} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => `€ ${v.toFixed(2)}`} contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
                <Legend />
                <Bar dataKey="ufficiale" stackId="a" fill="#3A7D44" name="Ufficiale" radius={[0, 0, 0, 0]} />
                <Bar dataKey="black" stackId="a" fill="#E0A11B" name="Preventivi (black)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-muted-foreground mt-2">L'altezza totale della barra è l'incasso totale (ufficiale + preventivi).</p>
        </Card>
      )}

      {canAccounting && trend.length > 0 && (
        <Card className="mt-6 p-6 rounded-xl" data-testid="trend-chart-card">
          <h3 className="font-dash font-semibold text-lg mb-4">Andamento incassi e spese (14 giorni)</h3>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3A7D44" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#3A7D44" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C24127" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#C24127" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#5C5C54" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#5C5C54" }} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => `€ ${v.toFixed(2)}`} contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
                <Area type="monotone" dataKey="incassi" stroke="#3A7D44" strokeWidth={2} fill="url(#inc)" name="Incassi" />
                <Area type="monotone" dataKey="spese" stroke="#C24127" strokeWidth={2} fill="url(#exp)" name="Spese" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Stat icon={UtensilsCrossed} label="Piatti a menù" value={s.dishes_count} color="text-primary" testid="stat-dishes" />
        {canAccounting && <Stat icon={TrendingUp} label="Incassi totali" value={fmt(s.income_total)} color="text-[hsl(var(--success))]" testid="stat-income-total" />}
      </div>
    </div>
  );
}
